<template>
  <div class="image-editor" v-if="visible">
    <div class="editor-container">
      <!-- 图片显示区域 -->
      <div class="image-container">
        <div class="image-wrapper" ref="imageWrapper">
          <!-- 使用 Vue Cropper 替换原来的 canvas -->
          <vue-cropper
            ref="cropper"
            :img="imageUrl"
            :outputSize="0.95"
            :outputType="outputType"
            :info="true"
            :full="false"
            :canMove="true"
            :canMoveBox="true"
            :original="false"
            :autoCrop="true"
            :autoCropWidth="cropWidth"
            :autoCropHeight="cropHeight"
            :fixed="true"
            :fixedNumber="[cropRatioWidth, cropRatioHeight]"
            :centerBox="true"
            :infoTrue="true"
            :high="true"
            :mode="cropMode"
            :maxImgSize="maxImgSize"
            :enlarge="1"
            :maxRealNum="1"
            :fillColor="fillColor"
            :limitMinSize="limitMinSize"
            @realTime="realTime"
            @imgLoad="imgLoad"
          />
        </div>
      </div>

      <!-- 控制按钮区域 -->
      <div class="control-panel">
        <div class="control-btn-group">
          <div class="control-btn" @click="rotateImage">
            <img src="@/assets/icons/icon-rotation.svg" alt="旋转" />
          </div>
          <div class="bottom-buttons">
            <div class="cancel-btn" @click="handleCancel">取消</div>
            <div class="confirm-btn" @click="handleConfirm">确定</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VueCropper } from 'vue-cropper'

export default {
  name: 'ImageEditor',
  components: {
    VueCropper
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    imageUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // 裁剪相关配置
      cropWidth: 100,
      cropHeight: 130,
      cropRatioWidth: 100,
      cropRatioHeight: 130,
      cropMode: 'contain', // 裁剪模式
      outputType: 'jpeg', // 输出格式
      maxImgSize: 2000, // 最大图片尺寸
      fillColor: '#000000', // 填充颜色
      limitMinSize: [50, 50], // 最小裁剪尺寸

      // 裁剪结果
      cropData: null
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initEditor()
      }
    }
  },
  methods: {
    initEditor() {
      // 初始化编辑器
      this.cropData = null
      this.$nextTick(() => {
        if (this.$refs.cropper) {
          // 设置初始裁剪框尺寸
          this.updateCropSize()
        }
      })
    },

    updateCropSize() {
      // 根据容器尺寸动态调整裁剪框大小
      if (this.$refs.imageWrapper) {
        const rect = this.$refs.imageWrapper.getBoundingClientRect()
        const containerWidth = rect.width
        const containerHeight = rect.height

        // 计算合适的裁剪框尺寸（保持100:130比例）
        const targetRatio = 100 / 130
        const maxWidth = Math.min(containerWidth * 0.6, containerWidth * 0.8)
        const maxHeight = Math.min(containerHeight * 0.6, containerHeight * 0.8)

        let cropWidth, cropHeight
        if (maxWidth / maxHeight > targetRatio) {
          cropHeight = maxHeight
          cropWidth = cropHeight * targetRatio
        } else {
          cropWidth = maxWidth
          cropHeight = cropWidth / targetRatio
        }

        this.cropWidth = Math.round(cropWidth)
        this.cropHeight = Math.round(cropHeight)
        this.cropRatioWidth = this.cropWidth
        this.cropRatioHeight = this.cropHeight
      }
    },

    rotateImage() {
      // 旋转图片
      if (this.$refs.cropper) {
        this.$refs.cropper.rotateRight()
      }
    },

    realTime(data) {
      // 实时裁剪数据
      this.cropData = data
    },

    imgLoad(msg) {
      // 图片加载完成
      console.log('Image loaded:', msg)
      this.updateCropSize()
    },

    handleCancel() {
      this.$emit('cancel')
    },

    async handleConfirm() {
      try {
        this.$toast.loading({
          message: '处理中...',
          forbidClick: true
        })

        // 使用 Vue Cropper 的裁剪方法，需要传入回调函数
        const result = await new Promise((resolve, reject) => {
          this.$refs.cropper.getCropData((data) => {
            resolve(data)
          })
        })

        const processedResult = {
          url: result,
          rotation: 0, // Vue Cropper 会自动处理旋转
          crop: this.cropData,
          originalUrl: this.imageUrl
        }

        this.$toast.clear()
        this.$emit('confirm', processedResult)
      } catch (error) {
        console.error('Crop error:', error)
        this.$toast.clear()
        this.$toast('图片处理失败')
      }
    }
  }
}
</script>

<style lang="less" scoped>
.image-editor {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  z-index: 2000;
  display: flex;
  flex-direction: column;
}

.editor-container {
  flex: 1;
  // height: 0;
  display: flex;
  flex-direction: column;
}

.image-container {
  flex: 1;
  // height: 0;
  position: relative;
  overflow: hidden;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;

  // Vue Cropper 样式调整
  :deep(.vue-cropper) {
    width: 100% !important;
    height: 100% !important;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    background-color: #000;
    background-image: none;
  }

  // 裁剪框样式 - 白色边框
  :deep(.cropper-view-box) {
    border: 1px solid #fff !important;
    border-radius: 0 !important;
    outline: none !important;
    box-shadow: none !important;
  }

  // 四个角的L型图标
  :deep(.crop-point) {
    opacity: 1;
    background: none !important;
  }

  // 左上角
  :deep(.crop-point.point1) {
    background-image: url('~@/assets/icons/crop-corner-top-left.svg') !important;
    background-repeat: no-repeat !important;
    background-size: contain !important;
    background-position: center !important;
    top: -1.7px !important;
    left: -3px !important;
  }

  // 右上角
  :deep(.crop-point.point3) {
    background-image: url('~@/assets/icons/crop-corner-top-right.svg') !important;
    background-repeat: no-repeat !important;
    background-size: contain !important;
    background-position: center !important;
    top: -1.7px !important;
    right: -3px !important;
  }

  // 左下角
  :deep(.crop-point.point6) {
    background-image: url('~@/assets/icons/crop-corner-bottom-left.svg') !important;
    background-repeat: no-repeat !important;
    background-size: contain !important;
    background-position: center !important;
    bottom: -5px !important;
    left: -3px !important;
  }

  // 右下角
  :deep(.crop-point.point8) {
    background-image: url('~@/assets/icons/crop-corner-bottom-right.svg') !important;
    background-repeat: no-repeat !important;
    background-size: contain !important;
    background-position: center !important;
    bottom: -5px !important;
    right: -3px !important;
  }

  // 中间的控制点
  :deep(.cropper-point.point-mt),
  :deep(.cropper-point.point-mb),
  :deep(.cropper-point.point-ml),
  :deep(.cropper-point.point-mr) {
    background: rgba(255, 255, 255, 0.8) !important;
    border: 1px solid #fff !important;
    width: 8px !important;
    height: 8px !important;
    border-radius: 50% !important;
  }
}

.control-panel {
  background-color: #000;
  padding: 50/20rem 20/20rem 20/20rem;
  height: 182/20rem;
  flex-shrink: 0;

  .control-btn-group {
    display: flex;
    align-items: center;
  }
}

.control-btn {
  color: #fff;
  font-size: 14px;
  padding-right: 20/20rem;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  height: 24/20rem;

  img {
    width: 24/20rem;
    height: 24/20rem;
  }
}

.bottom-buttons {
  flex: 1;
  width: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.cancel-btn {
  color: #fff;
  font-size: 16/20rem;
  width: 74/20rem;
  height: 46/20rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.confirm-btn {
  color: #fff;
  font-size: 16/20rem;
  width: 160/20rem;
  height: 46/20rem;
  border: 1px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
